// Shared person-display helpers for the Competencies employee tiles. These keep the
// name/initials/subtitle rendering consistent across the roster, the assignment
// wizard, and the assigned-employees list, which each render a slightly different
// tile but share the same "name + job · department" grammar.

// Shared text-color constants for the tile design.
export const NAVY = 'var(--color-navy)';
export const MUTED = '#5E5E5E';

// The minimal shape these helpers need. Every employee/user type used by the tiles
// (RosterEmployee, EmployeePickerItem, directory User) structurally satisfies it.
export type NamedPerson = { firstName: string; lastName: string };

export const fullName = (person: NamedPerson): string =>
    `${person.firstName} ${person.lastName}`.trim();

export const hasName = (person: NamedPerson): boolean => fullName(person).length > 0;

// Initials for the tile avatar. Empty for a nameless person so the shared Avatar
// falls back to its generic grey person icon instead of an empty teal circle.
export const initials = (person: NamedPerson): string =>
    hasName(person)
        ? `${person.firstName.charAt(0)}${person.lastName.charAt(0)}`.toUpperCase()
        : '';

// Secondary tile line: localized "Job title · Department", resolved from the ids the
// employee/user already carries (no extra query). Omits a missing side gracefully and
// falls back to the caller-supplied placeholder when neither exists.
export const jobAndDepartmentLabel = (
    employee: { jobTitleId?: string | null; departmentId?: string | null },
    opts: {
        getJobTitle: (id: string) => string;
        getDepartment: (id: string) => string;
        fallback: string;
    },
): string =>
    [
        employee.jobTitleId ? opts.getJobTitle(employee.jobTitleId) : '',
        employee.departmentId ? opts.getDepartment(employee.departmentId) : '',
    ]
        .filter((part) => part.length > 0)
        .join(' · ') || opts.fallback;
